import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ProfileInfo from '../components/profile-info';
import ProjectSelectionScreen from '../../components/ProjectSelectionScreen';
import { getProjectState } from '../../services/dashboard';
import { clearCacheOnProjectChange } from '../../Common/menuCacheUtils';
import { useAuth } from '../../auth/AuthContext';

type ProgressLabel = 'Not started' | 'In progress' | 'Completed' | 'Error';

const Projects = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [projectState, setProjectState] = useState<{
    data?: { projectId: number; state: number }[];
  } | null>(null);

  useEffect(() => {
    const fetchProjectData = async () => {
      if (!user) return;

      try {
        setSelectedId(localStorage.getItem('projectId'));

        const projectList = user.projectDetails?.map((obj) => obj.projectId) ?? [];
        const projectInfo = await getProjectState({
          userId: user.userId,
          projectId: projectList,
        });
        setProjectState(projectInfo);
      } catch (error) {
        console.error('Error fetching project data:', error);
        setProjectState(null);
      }
    };

    fetchProjectData();
  }, [user]);

  const getProjectProgress = (projectId: number): ProgressLabel => {
    const state = projectState?.data?.find((item) => item.projectId === projectId);
    if (!state) {
      return projectState ? 'Error' : 'Not started';
    }
    if (state.state === 0) return 'Not started';
    if (state.state >= 1 && state.state <= 2) return 'In progress';
    return 'Completed';
  };

  const handleSelect = (projectId: number | string) => {
    localStorage.setItem('projectId', String(projectId));
    clearCacheOnProjectChange();
    navigate('/su/dashboard');
  };

  const projects = useMemo(
    () =>
      (user?.projectDetails ?? []).map((project) => ({
        id: project.projectId,
        name: project.projectName,
        companyName: project.companyName,
        status: getProjectProgress(project.projectId),
        selected: selectedId != null && String(project.projectId) === String(selectedId),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.projectDetails, projectState, selectedId]
  );

  return (
    <ProjectSelectionScreen
      title="Your projects"
      subtitle="Select a project to begin its evaluation and open your Super User workspace."
      projects={projects}
      onSelect={handleSelect}
      profileSlot={<ProfileInfo />}
    />
  );
};

export default Projects;
